import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { BookOpen, Calendar, ArrowRight, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndexPage() {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog`, {
          params: { lang: language, limit: 30 },
        });
        // Fall back to all languages if there are no posts in the active language yet.
        if (!data.posts.length && language !== 'lv') {
          const { data: all } = await axios.get(`${API}/blog`, { params: { limit: 30 } });
          setPosts(all.posts);
          setTotal(all.total);
        } else {
          setPosts(data.posts);
          setTotal(data.total);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [language]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(
      language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' }
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="blog-index-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#F3F2EE] to-[#FAFAFA] py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 bg-[#D97736]/10 rounded-2xl flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-[#D97736]" />
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-[#121212] mb-3">
            {language === 'lv' ? 'KoLV Blogs' : language === 'ru' ? 'KoLV Блог' : 'KoLV Blog'}
          </h1>
          <p className="text-[#5C5C5C] max-w-2xl mx-auto">
            {language === 'lv'
              ? 'Padomi, idejas un stāsti par latviešu vērtīgākajām lietām — produkti, pakalpojumi un pieredze.'
              : language === 'ru'
              ? 'Советы, идеи и истории о самом ценном у латышей — продуктах, услугах и опыте.'
              : 'Tips, ideas, and stories about what Latvians value most — products, services, and experiences.'}
          </p>
        </div>
      </div>

      {/* Posts */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-[#D97736] animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-[#5C5C5C]">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>{language === 'lv' ? 'Drīzumā šeit būs jauni raksti!' : language === 'ru' ? 'Скоро здесь появятся новые статьи!' : 'New articles coming soon!'}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#8A8A8A] mb-6" data-testid="blog-total">
              {language === 'lv' ? `${total} raksti` : language === 'ru' ? `${total} статей` : `${total} articles`}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-2xl border border-[#E5E4E0] overflow-hidden hover:shadow-lg hover:border-[#D97736] transition-all"
                  data-testid={`blog-post-card-${post.slug}`}
                >
                  {post.hero_image_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-[#F3F2EE]">
                      <img
                        src={post.hero_image_url}
                        alt={post.title}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#F3F2EE] to-[#E5E4E0] flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-[#D97736]/40" />
                    </div>
                  )}
                  <div className="p-5">
                    <h2 className="font-outfit font-semibold text-lg text-[#121212] mb-2 line-clamp-2 group-hover:text-[#D97736] transition-colors">
                      {post.title}
                    </h2>
                    {post.meta_description && (
                      <p className="text-sm text-[#5C5C5C] line-clamp-3 mb-3">
                        {post.meta_description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-[#8A8A8A]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.created_at)}
                      </span>
                      <ArrowRight className="w-4 h-4 text-[#D97736] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
